/**
 * AddPlantEnum Component
 * 
 * A comprehensive dialog component for adding new plant varieties to the system.
 * Manages complex plant data including:
 * - Basic info: name, scientific name, subtitle, description
 * - Categorization
 * - Growth timeline data (baby, vegetative, flowering stages)
 * - Images: icon, main image, and image gallery (up to 5 images)
 * - Characteristics: colors, flavours, nutrients, kitchen uses, allergens
 * - Edibility status
 * 
 * Features:
 * - Image upload with automatic compression/resizing
 * - Dynamic list management for multi-value fields
 * - Form validation
 * - Firebase Storage and Firestore integration
 * - Snackbar notifications for user feedback
 */
import React, { useState } from "react";

import { doc, collection, addDoc, updateDoc } from "firebase/firestore";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Switch, Snackbar } from "@mui/material";
import Resizer from "react-image-file-resizer";

import {
  ref,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";

import { ImageLoader } from "../../../../components/ui/img/ImageLoader";
import { firebaseStorage } from "../../../../components/firebase/storage";
import { firebaseStore } from "../../../../components/firebase/firestore";
import { firebaseAuth } from "../../../../components/firebase/auth";
import { primaryColorTextField } from "../../../../components/theme/globalStyles";
import { plantCategoryEnums } from "../../../../components/utils/enums";

import "./PlantEnumsComp.css";

function AddPlantEnum() {
  const [user, loading, error] = useAuthState(firebaseAuth);
  const navigate = useNavigate();
  
  /**
   * Dropzone configuration for icon upload
   * Accepts: JPEG, PNG
   * Max files: 1
   */
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 1,
    onDrop: (files) => onIconDrop(files),
  });
  
  /**
   * Dropzone configuration for main image upload
   * Accepts: JPEG, PNG
   * Max files: 1
   */
  const { getRootProps: getRootPropsImage, getInputProps: getInputPropsImage } =
    useDropzone({
      accept: {
        "image/jpeg": [],
        "image/png": [],
      },
      maxFiles: 1,
      onDrop: (files) => onImageDrop(files),
    });
  
  /**
   * Dropzone configuration for image gallery
   * Accepts: JPEG, PNG
   * Max files: 5 total
   */
  const {
    getRootProps: getRootPropsImageArray,
    getInputProps: getInputPropsImageArray,
  } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 5,
    onDrop: (files) => onImageDropArray(files),
  });
  
  // Dialog and UI state
  const [open, setOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Basic plant information state
  const [plantName, setPlantName] = useState<string>("");
  const [scientificName, setScientificName] = useState<string>("");
  const [plantSubtitle, setPlantSubtitle] = useState<string>("");
  const [plantDescription, setPlantDescription] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  
  // Growth timeline state (in days)
  const [growTimeBabyMin, setGrowTimeBabyMin] = useState<number>(3);
  const [growTimeBabyMax, setGrowTimeBabyMax] = useState<number>(14);
  const [growTimeVegetativeMin, setGrowTimeVegetativeMin] = useState<number>(20);
  const [growTimeVegetativeMax, setGrowTimeVegetativeMax] = useState<number>(30);
  const [floweringMin, setFloweringMin] = useState<number>(30);
  const [floweringMax, setFloweringMax] = useState<number>(40);
  
  // Image upload state
  const [iconUrl, setIconUrl] = useState<string>("");
  const [iconPath, setIconPath] = useState<any>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imagePath, setImagePath] = useState<any>(null);
  const [imageUrlArray, setImageUrlArray] = useState<string[]>([]);
  const [imagePathArray, setImagePathArray] = useState<any[]>([]);
  
  // Multi-value characteristic states
  // Each has a "List" (stored values) and "Actual" (current input being typed)
  const [nutrientActual, setNutrientActual] = useState<string>("");
  const [nutrientList, setNutrientList] = useState<string[]>([]);
  const [kitchenList, setKitchenList] = useState<string[]>([]);
  const [kitchenActual, setKitchenActual] = useState<string>("");
  const [flavourList, setFlavourList] = useState<string[]>([]);
  const [flavourActual, setFlavourActual] = useState<string>("");
  const [colorList, setColorList] = useState<string[]>([]);
  const [colorActual, setColorActual] = useState<string>("");
  const [allergeenList, setAllergeenList] = useState<string[]>([]);
  const [allergeenActual, setAllergeenActual] = useState<string>("");
  
  // Edibility toggle
  const [isEdible, setIsEdible] = useState<boolean>(false);

  /**
   * Close the add plant dialog
   */
  const handleClose = () => {
    setOpen(false);
  };

  /**
   * Add a text value to a list (generic function for all list fields)
   * Validates:
   * - Not empty
   * - Not duplicate
   * - Length between 2-1000 characters
   * - List max size of 10 items
   * 
   * @param value - The string value to add
   * @param list - Current list state
   * @param setList - Function to update the list
   * @param setValue - Function to clear the input field
   */
  const handleAddToList = (
    value: string,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    setValue: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const tempList = [...list];
    if (
      value !== "" &&
      !tempList.includes(value) &&
      value.length > 2 &&
      value.length < 1000 &&
      tempList.length < 10
    ) {
      tempList.push(value);
      setList(tempList);
      setValue("");
    }
  };

  /**
   * Remove a value from a list by index
   * 
   * @param index - Array index to remove
   * @param list - Current list state
   * @param setList - Function to update the list
   */
  const handleRemoveFromList = (
    index: number,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const tempList = [...list];
    tempList.splice(index, 1);
    setList(tempList);
  };

  /**
   * Handle icon image drop from dropzone
   * @param files - Array of dropped files (max 1 for icon)
   */
  const onIconDrop = async (files: any) => {
    const file = files[0];
    setIconPath(file);
  };

  /**
   * Handle main image drop from dropzone
   * @param files - Array of dropped files (max 1 for main image)
   */
  const onImageDrop = async (files: any) => {
    const file = files[0];
    setImagePath(file);
  };

  /**
   * Handle image gallery drop from dropzone
   * Validates max 5 images total
   * @param files - Array of dropped files
   */
  const onImageDropArray = async (files: any) => {
    if (files.length > 5) {
      return;
    }
    if (files.length + imagePathArray.length > 5) {
      return;
    }
    const tempArray = [...imagePathArray];
    files.forEach((file: any) => {
      tempArray.push(file);
    });
    setImagePathArray(tempArray);
  };

  /**
   * Remove an image from the image gallery array
   * @param index - Index of image to remove
   */
  const onDeleteFromImageArray = (index: number) => {
    const tempList = [...imagePathArray];
    tempList.splice(index, 1);
    setImagePathArray(tempList);
  };

  /**
   * Reset all form fields to their initial/default values
   * Called after successful submission or user cancellation
   */
  const onReset = () => {
    setPlantName("");
    setScientificName("");
    setPlantSubtitle("");
    setPlantDescription("");
    setCategory("");
    setGrowTimeBabyMin(3);
    setGrowTimeBabyMax(14);
    setGrowTimeVegetativeMin(20);
    setGrowTimeVegetativeMax(30);
    setFloweringMin(30);
    setFloweringMax(40);
    setColorList([]);
    setFlavourList([]);
    setNutrientList([]);
    setKitchenList([]);
    setAllergeenList([]);
    setIsEdible(false);
    setIconPath(null);
    setImagePath(null);
    setImagePathArray([]);
  };

  /**
   * Resize image file using react-image-file-resizer
   * Compresses and resizes images before upload to reduce storage costs
   * 
   * @param file - Original image file
   * @param size - Target size (width and height in pixels)
   * @param type - Image format ('png' or 'jpeg')
   * @returns Promise resolving to resized blob
   */
  const resizeFile = (file: any, size: number, type: string) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        size,
        size,
        type,
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "blob"
      );
    });

  /**
   * Check if an image already exists in Firebase Storage
   * @param path - Storage path to check
   * @returns Promise<boolean> - true if exists, false otherwise
   */
  const checkIfLogoExists = async (path: string) => {
    try {
      const imageRef = ref(firebaseStorage, path);
      await getDownloadURL(imageRef);
      return true;
    } catch (err) {
      return false;
    }
  };

  /**
   * Upload an image to Firebase Storage with automatic resizing and compression
   * If file exists at path, it will be deleted first
   * 
   * @param file - Image file to upload
   * @param path - Firebase Storage path
   * @param size - Target size in pixels (square)
   * @param type - Image format ('png' or 'jpeg')
   * @returns Promise<string> - Download URL of uploaded image, or empty string on error
   */
  const uploadImage = async (
    file: any,
    path: string,
    size: number,
    type: string
  ) => {
    const storageRef = ref(firebaseStorage, path);
    try {
      const checkIfExists = await checkIfLogoExists(path);
      if (checkIfExists) {
        await deleteObject(storageRef);
      }
      const resizedFile: any = await resizeFile(file, size, type);
      await uploadBytes(storageRef, resizedFile);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.log(error);
      return "";
    }
  };

  /**
   * Main function to upload plant enum data to Firestore and Storage
   * 
   * Process:
   * 1. Creates Firestore document with plant data
   * 2. Gets document ID
   * 3. Uploads icon image (if provided) to Storage
   * 4. Uploads main image (if provided) to Storage
   * 5. Uploads image gallery (if provided) to Storage
   * 6. Updates Firestore document with image URLs
   * 7. Resets form and shows success notification
   * 
   * Images are automatically resized:
   * - Icon: 500x500px PNG
   * - Main image: 1000x1000px JPEG
   * - Gallery images: 1000x1000px JPEG each
   */
  const uploadPlantEnum = async () => {
    setDialogLoading(true);
    try {
      const plantEnum = {
        name: plantName,
        scientificName: scientificName,
        subtitle: plantSubtitle,
        description: plantDescription,
        category: category,
        growTimeBaby: {
          min: growTimeBabyMin,
          max: growTimeBabyMax,
        },
        growTimeVegetative: {
          min: growTimeVegetativeMin,
          max: growTimeVegetativeMax,
        },
        growTimeFlowering: {
          min: floweringMin,
          max: floweringMax,
        },
        color: colorList,
        flavour: flavourList,
        nutrients: nutrientList,
        kitchen: kitchenList,
        allergeens: allergeenList,
        isEdible: isEdible,
      };
      const docRef = await addDoc(
        collection(firebaseStore, "plantEnums"),
        plantEnum
      );
      const docId = docRef.id;
      let iconUrl = "";
      let imageUrl = "";
      let imageUrlArray = [];
      if (iconPath) {
        const iconPathString = `cloud/plantEnums/${docId}/${docId}-icon.png`;
        const uploadIcon = await uploadImage(
          iconPath,
          iconPathString,
          500,
          "png"
        );
        iconUrl = uploadIcon;
      }
      if (imagePath) {
        const imagePathString = `cloud/plantEnums/${docId}/${docId}.jpeg`;
        const uploadImageTemp = await uploadImage(
          imagePath,
          imagePathString,
          1000,
          "jpeg"
        );
        imageUrl = uploadImageTemp;
      }
      if (imagePathArray.length > 0) {
        for (let i = 0; i < imagePathArray.length; i++) {
          const imagePathString = `cloud/plantEnums/${docId}/${docId}-${
            i + 1
          }.jpeg`;
          const uploadImageTemp = await uploadImage(
            imagePathArray[i],
            imagePathString,
            1000,
            "jpeg"
          );
          imageUrlArray.push(uploadImageTemp);
        }
      }
      await updateDoc(doc(collection(firebaseStore, "plantEnums"), docId), {
        icon: iconUrl,
        image: imageUrl,
        images: imageUrlArray,
        id: docId,
      });

      setSnackbarOpen(true);
      setSnackbarMessage(`${plantName} added to plant enums`);
      setDialogLoading(false);
    } catch (error) {
      console.log(error);
      setDialogLoading(false);
    }
  };

  return (
    <div className="AddPlantEnum">
      <IconButton sx={{ margin: 0 }} onClick={() => setOpen(true)}>
        <AddIcon style={{ color: "#44a99a" }} />
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-paper": {
            width: "90vw",
            maxWidth: "1000px",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <div className="AddPlantEnumDialog">
          <DialogTitle>Add plant enum</DialogTitle>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Plant name"
              variant="outlined"
              value={plantName}
              onChange={(e) => setPlantName(e.target.value)}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Scientific name"
              variant="outlined"
              value={scientificName}
              onChange={(e) => setScientificName(e.target.value)}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Plant subtitle"
              variant="outlined"
              value={plantSubtitle}
              onChange={(e) => setPlantSubtitle(e.target.value)}
              multiline
              rows={2}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Plant description"
              variant="outlined"
              value={plantDescription}
              onChange={(e) => setPlantDescription(e.target.value)}
              multiline
              rows={4}
            />
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Category"
              variant="outlined"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              select
            >
              {plantCategoryEnums.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div
            className="AddPlantEnumDialogOuter"
            style={{ marginTop: "40px" }}
          >
            <p className="AddPlantEnumDialogOuterTitle">
              Grow time seedling or cutting
            </p>
            <div className="AddPlantEnumDialogInner">
              <div className="AddPlantEnumDialogCounter">
                <p className="AddPlantEnumDialogOuterSub">Minimum days</p>
                <div className="AddPlantEnumDialogCounterInner">
                  <IconButton
                    onClick={() => {
                      if (
                        growTimeBabyMin - 1 >= 0 &&
                        growTimeBabyMin <= growTimeBabyMax
                      ) {
                        setGrowTimeBabyMin(growTimeBabyMin - 1);
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    sx={{
                      ...primaryColorTextField,
                      width: "80px",
                    }}
                    size="small"
                    inputProps={{ style: { textAlign: "center" } }}
                    variant="outlined"
                    value={growTimeBabyMin}
                    onChange={(e) => {
                      if (
                        Number(e.target.value) >= 0 &&
                        Number(e.target.value) <= growTimeBabyMax
                      ) {
                        setGrowTimeBabyMin(Number(e.target.value));
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      if (growTimeBabyMin < growTimeBabyMax) {
                        setGrowTimeBabyMin(growTimeBabyMin + 1);
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
              <div className="AddPlantEnumDialogCounter">
                <p className="AddPlantEnumDialogOuterSub">Maximum days</p>
                <div className="AddPlantEnumDialogCounterInner">
                  <IconButton
                    onClick={() => {
                      if (growTimeBabyMax - 1 >= growTimeBabyMin) {
                        setGrowTimeBabyMax(growTimeBabyMax - 1);
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    sx={{
                      ...primaryColorTextField,
                      width: "80px",
                    }}
                    size="small"
                    inputProps={{ style: { textAlign: "center" } }}
                    variant="outlined"
                    value={growTimeBabyMax}
                    onChange={(e) => {
                      if (
                        Number(e.target.value) >= growTimeBabyMin &&
                        Number(e.target.value) >= 0
                      ) {
                        setGrowTimeBabyMax(Number(e.target.value));
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      if (growTimeBabyMax >= growTimeBabyMin) {
                        setGrowTimeBabyMax(growTimeBabyMax + 1);
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
          <div className="AddPlantEnumDialogOuter">
            <p className="AddPlantEnumDialogOuterTitle">Grow time vegetative</p>
            <div className="AddPlantEnumDialogInner">
              <div className="AddPlantEnumDialogCounter">
                <p className="AddPlantEnumDialogOuterSub">Minimum days</p>
                <div className="AddPlantEnumDialogCounterInner">
                  <IconButton
                    onClick={() => {
                      if (
                        growTimeVegetativeMin - 1 >= 0 &&
                        growTimeVegetativeMin <= growTimeVegetativeMax
                      ) {
                        setGrowTimeVegetativeMin(growTimeVegetativeMin - 1);
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    sx={{
                      ...primaryColorTextField,
                      width: "80px",
                    }}
                    size="small"
                    inputProps={{ style: { textAlign: "center" } }}
                    variant="outlined"
                    value={growTimeVegetativeMin}
                    onChange={(e) => {
                      if (
                        Number(e.target.value) >= 0 &&
                        Number(e.target.value) <= growTimeVegetativeMax
                      ) {
                        setGrowTimeVegetativeMin(Number(e.target.value));
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      if (growTimeVegetativeMin < growTimeVegetativeMax) {
                        setGrowTimeVegetativeMin(growTimeVegetativeMin + 1);
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
              <div className="AddPlantEnumDialogCounter">
                <p className="AddPlantEnumDialogOuterSub">Maximum days</p>
                <div className="AddPlantEnumDialogCounterInner">
                  <IconButton
                    onClick={() => {
                      if (growTimeVegetativeMax - 1 >= growTimeVegetativeMin) {
                        setGrowTimeVegetativeMax(growTimeVegetativeMax - 1);
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    sx={{
                      ...primaryColorTextField,
                      width: "80px",
                    }}
                    size="small"
                    inputProps={{ style: { textAlign: "center" } }}
                    variant="outlined"
                    value={growTimeVegetativeMax}
                    onChange={(e) => {
                      if (
                        Number(e.target.value) >= growTimeVegetativeMin &&
                        Number(e.target.value) >= 0
                      ) {
                        setGrowTimeVegetativeMax(Number(e.target.value));
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      if (growTimeVegetativeMax >= growTimeVegetativeMin) {
                        setGrowTimeVegetativeMax(growTimeVegetativeMax + 1);
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
          <div className="AddPlantEnumDialogOuter">
            <p className="AddPlantEnumDialogOuterTitle">Grow time flowering</p>
            <div className="AddPlantEnumDialogInner">
              <div className="AddPlantEnumDialogCounter">
                <p className="AddPlantEnumDialogOuterSub">Minimum days</p>
                <div className="AddPlantEnumDialogCounterInner">
                  <IconButton
                    onClick={() => {
                      if (
                        floweringMin - 1 >= 0 &&
                        floweringMin <= floweringMax
                      ) {
                        setFloweringMin(floweringMin - 1);
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    sx={{
                      ...primaryColorTextField,
                      width: "80px",
                    }}
                    size="small"
                    inputProps={{ style: { textAlign: "center" } }}
                    variant="outlined"
                    value={floweringMin}
                    onChange={(e) => {
                      if (
                        Number(e.target.value) >= 0 &&
                        Number(e.target.value) <= floweringMax
                      ) {
                        setFloweringMin(Number(e.target.value));
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      if (floweringMin < floweringMax) {
                        setFloweringMin(floweringMin + 1);
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
              <div className="AddPlantEnumDialogCounter">
                <p className="AddPlantEnumDialogOuterSub">Maximum days</p>
                <div className="AddPlantEnumDialogCounterInner">
                  <IconButton
                    onClick={() => {
                      if (floweringMax - 1 >= floweringMin) {
                        setFloweringMax(floweringMax - 1);
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    sx={{
                      ...primaryColorTextField,
                      width: "80px",
                    }}
                    size="small"
                    inputProps={{ style: { textAlign: "center" } }}
                    variant="outlined"
                    value={floweringMax}
                    onChange={(e) => {
                      if (
                        Number(e.target.value) >= floweringMin &&
                        Number(e.target.value) >= 0
                      ) {
                        setFloweringMax(Number(e.target.value));
                      }
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      if (floweringMax >= floweringMin) {
                        setFloweringMax(floweringMax + 1);
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          </div>
          <div
            className="AddPlantEnumDialogOuter"
            style={{ marginTop: "40px" }}
          >
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Color"
                variant="outlined"
                value={colorActual}
                onChange={(e) => setColorActual(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() =>
                  handleAddToList(
                    colorActual,
                    colorList,
                    setColorList,
                    setColorActual
                  )
                }
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogList">
              {colorList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">{item}</p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() =>
                      handleRemoveFromList(index, colorList, setColorList)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div className="AddPlantEnumDialogOuter">
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Flavour"
                variant="outlined"
                value={flavourActual}
                onChange={(e) => setFlavourActual(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() =>
                  handleAddToList(
                    flavourActual,
                    flavourList,
                    setFlavourList,
                    setFlavourActual
                  )
                }
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogList">
              {flavourList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">{item}</p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() =>
                      handleRemoveFromList(index, flavourList, setFlavourList)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div className="AddPlantEnumDialogOuter">
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Nutrients"
                variant="outlined"
                value={nutrientActual}
                onChange={(e) => setNutrientActual(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() =>
                  handleAddToList(
                    nutrientActual,
                    nutrientList,
                    setNutrientList,
                    setNutrientActual
                  )
                }
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogList">
              {nutrientList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">{item}</p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() =>
                      handleRemoveFromList(index, nutrientList, setNutrientList)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div className="AddPlantEnumDialogOuter">
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Kitchen"
                variant="outlined"
                value={kitchenActual}
                onChange={(e) => setKitchenActual(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() =>
                  handleAddToList(
                    kitchenActual,
                    kitchenList,
                    setKitchenList,
                    setKitchenActual
                  )
                }
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogList">
              {kitchenList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">{item}</p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() =>
                      handleRemoveFromList(index, kitchenList, setKitchenList)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div className="AddPlantEnumDialogOuter">
            <div className="AddPlantEnumDialogInner">
              <TextField
                sx={primaryColorTextField}
                size="small"
                fullWidth
                label="Allergeens"
                variant="outlined"
                value={allergeenActual}
                onChange={(e) => setAllergeenActual(e.target.value)}
              />
              <IconButton
                sx={{ marginLeft: "10px" }}
                onClick={() =>
                  handleAddToList(
                    allergeenActual,
                    allergeenList,
                    setAllergeenList,
                    setAllergeenActual
                  )
                }
              >
                <AddIcon sx={{ color: "#44a99a" }} />
              </IconButton>
            </div>
            <div className="AddPlantEnumDialogList">
              {allergeenList.map((item, index) => (
                <div key={index} className="AddPlantEnumDialogListItem">
                  <p className="AddPlantEnumDialogListItemText">{item}</p>
                  <IconButton
                    sx={{ marginLeft: "10px" }}
                    onClick={() =>
                      handleRemoveFromList(
                        index,
                        allergeenList,
                        setAllergeenList
                      )
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
          <div
            className="AddPlantEnumDialogInner"
            style={{ marginTop: "40px" }}
          >
            <p className="AddPlantEnumDialogSwitchText">Is edible</p>
            <Switch
              checked={isEdible}
              onChange={(e) => setIsEdible(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#44a99a",
                },

                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#44a99a",
                },
              }}
            />
          </div>
          <div
            className="AddPlantEnumDialogOuter"
            style={{ marginTop: "40px" }}
          >
            <p className="AddPlantEnumDialogOuterTitle">Icon</p>
            <div {...getRootProps({ className: "Dropzone" })}>
              <input {...getInputProps()} />
              <p
                className="AddPlantEnumDialogOuterSub"
                style={{ marginBottom: "10px" }}
              >
                Drag 'n' drop a Icon-image here, or click to select
              </p>
            </div>
            {iconPath && (
              <div className="AddPlantEnumDialogList">
                <p className="AddPlantEnumDialogOuterTitle">
                  Chosen icon - Click to delete
                </p>
                <div
                  onClick={() => setIconPath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader
                    imgUrl={URL.createObjectURL(iconPath)}
                    radiusStyle={"5px"}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="AddPlantEnumDialogOuter">
            <p className="AddPlantEnumDialogOuterTitle">Thumbnail</p>
            <div {...getRootPropsImage({ className: "Dropzone" })}>
              <input {...getInputPropsImage()} />
              <p className="AddPlantEnumDialogOuterSub">
                Drag 'n' drop a thumbnail-image here, or click to select
              </p>
            </div>
            {imagePath && (
              <div className="AddPlantEnumDialogList">
                <p
                  className="AddPlantEnumDialogOuterTitle"
                  style={{ marginBottom: "10px" }}
                >
                  Chosen thumbnail - Click to delete
                </p>
                <div
                  onClick={() => setImagePath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader
                    imgUrl={URL.createObjectURL(imagePath)}
                    radiusStyle={"5px"}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="AddPlantEnumDialogOuter">
            <p className="AddPlantEnumDialogOuterTitle">Images</p>
            <div {...getRootPropsImageArray({ className: "Dropzone" })}>
              <input {...getInputPropsImageArray()} />
              <p className="AddPlantEnumDialogOuterSub">
                Drag 'n' drop up to 5 images here, or click to select
              </p>
            </div>
            {imagePathArray.length > 0 && (
              <p className="AddPlantEnumDialogOuterTitle">
                Chosen images - Click to delete
              </p>
            )}
            {imagePathArray.length > 0 && (
              <div className="AddPlantEnumDialogImageList">
                {imagePathArray.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => onDeleteFromImageArray(index)}
                    className="AddPlantEnumDialogListImage"
                  >
                    <ImageLoader
                      imgUrl={URL.createObjectURL(item)}
                      radiusStyle={"5px"}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className="AddPlantEnumDialogInner"
            style={{ marginTop: "60px", justifyContent: "flex-end" }}
          >
            <Button
              disabled={dialogLoading}
              sx={{ marginLeft: "20px", marginRight: "20px" }}
              color="inherit"
              onClick={onReset}
            >
              RESET
            </Button>
            <Button
              disabled={dialogLoading}
              sx={{ marginLeft: "20px", marginRight: "20px" }}
              color="inherit"
              onClick={uploadPlantEnum}
            >
              {dialogLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "ADD"
              )}
            </Button>
          </div>
        </div>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        key={"bottom" + "center"}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
      />
    </div>
  );
}

export default AddPlantEnum;
